import { useEffect, useState, useCallback } from "react";
import { getTheme } from "@/lib/api";

/**
 * useTheme — fetches owner's theme from backend, exposes apply() that writes
 * CSS variables on document.documentElement so the whole app re-skins live.
 *
 * Also listens to "rawco-theme-changed" so voice actions can poke for a refresh.
 */
const BACKGROUNDS = {
  tinted_black:   { base: "linear-gradient(180deg, #03070f 0%, #050913 60%, #02060d 100%)", tint: "rgba(36, 80, 140, 0.22)" },
  pure_black:     { base: "#000000", tint: "transparent" },
  midnight_blue:  { base: "linear-gradient(180deg, #050a1a 0%, #08123a 60%, #040816 100%)", tint: "rgba(60, 110, 200, 0.18)" },
  dark_purple:    { base: "linear-gradient(180deg, #0c0518 0%, #1a0a36 60%, #060214 100%)", tint: "rgba(130, 80, 220, 0.18)" },
  deep_green:     { base: "linear-gradient(180deg, #04120c 0%, #0a2418 60%, #020c08 100%)", tint: "rgba(80, 200, 130, 0.15)" },
  galaxy:         { base: "#000000", tint: "transparent" },
  waveform:       { base: "url('/bg/waveform.jpg') center/cover no-repeat fixed, #000", tint: "rgba(20, 200, 220, 0.10)", animated: true },
  neural_brain:   { base: "url('/bg/brain.jpg') center/cover no-repeat fixed, #000",    tint: "rgba(20, 200, 220, 0.08)", animated: true },
  data_grid:      { base: "url('/bg/grid.jpg') center/cover no-repeat fixed, #000",     tint: "rgba(60, 160, 220, 0.08)", animated: true },
};

const ACCENTS = {
  gold:           { main: "#d4af37", soft: "rgba(212,175,55,0.55)", dim: "rgba(212,175,55,0.30)" },
  silver:         { main: "#c9d1d9", soft: "rgba(201,209,217,0.55)", dim: "rgba(201,209,217,0.30)" },
  bronze:         { main: "#cd7f32", soft: "rgba(205,127,50,0.55)", dim: "rgba(205,127,50,0.30)" },
  emerald:        { main: "#50c878", soft: "rgba(80,200,120,0.55)", dim: "rgba(80,200,120,0.30)" },
  crimson:        { main: "#dc143c", soft: "rgba(220,20,60,0.55)", dim: "rgba(220,20,60,0.30)" },
  electric_blue:  { main: "#3a86ff", soft: "rgba(58,134,255,0.55)", dim: "rgba(58,134,255,0.30)" },
  violet:         { main: "#a44dff", soft: "rgba(164,77,255,0.55)", dim: "rgba(164,77,255,0.30)" },
};

export default function useTheme() {
  const [theme, setTheme] = useState(null);

  const apply = useCallback((t) => {
    if (!t) return;
    const bg = BACKGROUNDS[t.background] || BACKGROUNDS.tinted_black;
    const acc = ACCENTS[t.accent] || ACCENTS.gold;
    const root = document.documentElement;
    // The pre-existing CSS uses --rc-gold for accents; we re-bind it to the chosen accent.
    root.style.setProperty("--rc-gold", acc.main);
    root.style.setProperty("--rc-gold-dim", acc.dim);
    root.style.setProperty("--rc-accent", acc.main);
    root.style.setProperty("--rc-accent-soft", acc.soft);
    root.style.setProperty("--rc-bg", typeof bg.base === "string" ? bg.base : "");
    root.style.setProperty("--rc-bg-tint", bg.tint);
    // Direct html background — guarantees the colour wins even if a stylesheet
    // forgets to use --rc-bg. The CSS grid overlay sits above this.
    document.documentElement.style.background = bg.base;
    document.body.style.background = "transparent";
    // Grid intensity CSS var (StageEffects + JarvisOrb pick it up)
    root.style.setProperty("--rc-grid-alpha", String(t.grid_intensity ?? 0.08));
    root.style.setProperty("--rc-grid-style", t.grid_style || "graph");
    // Hide the grid entirely when grid_style === "none"
    document.body.setAttribute("data-grid", t.grid_style === "none" ? "none" : "on");
    // Animated photo-backgrounds (waveform/neural_brain/data_grid) get a
    // body-class that drives the slow ken-burns zoom + pan via CSS.
    if (bg.animated) {
      document.body.classList.add("rc-bg-animated");
      document.body.setAttribute("data-bg", t.background || "");
      // Set the image URL via CSS var so webpack doesn't try to resolve it
      // at build time as a module. /bg/*.jpg lives in /public/ and is served
      // from the site root, so a literal /bg/file.jpg URL works fine.
      const imgMap = { waveform: "/bg/waveform.jpg", neural_brain: "/bg/brain.jpg", data_grid: "/bg/grid.jpg" };
      const file = imgMap[t.background];
      if (file) document.body.style.setProperty("--rc-bg-img", `url("${file}")`);
    } else {
      document.body.classList.remove("rc-bg-animated");
      document.body.removeAttribute("data-bg");
      document.body.style.removeProperty("--rc-bg-img");
    }
    // Persist quick-access copies for hooks that read localStorage
    if (t.orb_style) localStorage.setItem("rawco_orb_style", t.orb_style);
    if (t.lightning_color) localStorage.setItem("rawco_lightning_color", t.lightning_color);
    if (t.lightning_frequency) localStorage.setItem("rawco_lightning_frequency", t.lightning_frequency);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const t = await getTheme();
      setTheme(t);
      apply(t);
      return t;
    } catch (e) {
      return null;
    }
  }, [apply]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("rawco-theme-changed", onChange);
    return () => window.removeEventListener("rawco-theme-changed", onChange);
  }, [refresh]);

  return { theme, refresh, apply };
}
